/*
 * Copyright (c) 2023. Legalbird eine Marke der R+S Internet Jewels GmbH
 */

import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import { getProductName, isDocumentProduct } from "../../../services/Product/ProductService/productService";
import { Configure, InstantSearch } from "react-instantsearch";
import { liteClient } from "algoliasearch/lite";
import BoardRefinements from "../../Board/Refinement/BoardRefinements";
import LegalbirdHits from "../../Board/Hits/LegalbirdHits";
import { ALGOLIA_APPLICATION_ID, ALGOLIA_INDEX, ALGOLIA_SEARCH_API_KEY } from "../../../config/_entrypoint";
import { boardPageStyles } from "./boardPageStyles";
import { getBoardHeadline, getFilterString, getRefinements, getStagesForBoard } from "../../../services/Board/algolia";
import { useNavigate, useParams } from "react-router-dom";
import ValidatorSelect from "../../Validator/ValidatorSelect";
import { Box, Typography } from "@mui/material";
import RightBarPlaceholder from "../../Layout/RightBarPlaceholder";
import { useBackofficeUser } from "../../../provider/BackofficeUserProvider";
import { useTechStatus } from "../../../provider/TechStatusProvider";
import { AlgoliaRefinements } from "../../../types/Algolia/AlgoliaRefinements";
import usePartnerRegistry from "../../../hooks/usePartnerRegistry";

const searchClient = liteClient(ALGOLIA_APPLICATION_ID, ALGOLIA_SEARCH_API_KEY);

const BoardPage = () => {
  const { productUrlPart, caseType } = useParams<string>();
  const { accountManagers, lawyers } = useBackofficeUser();
  const { activePartners, isLoading: isPartnerRegistryLoading } = usePartnerRegistry();

  const techStatus = useTechStatus();

  const refinements: AlgoliaRefinements = getRefinements({ accountManagers, lawyers, activePartners });
  const filterString = productUrlPart && caseType ? getFilterString(productUrlPart, caseType) : "";

  const CaseTypeSelection = ({ caseType, productUrlPart }: { caseType: string; productUrlPart: string }) => {
    const navigate = useNavigate();
    if (isDocumentProduct(getProductName(productUrlPart))) {
      return null;
    }
    return (
      <ValidatorSelect
        label="Fallstatus"
        value={caseType}
        onChange={(event) => {
          navigate("/services/board/" + productUrlPart + "/" + event.target.value);
        }}
      >
        <MenuItem value={"offen"}>Offene Fälle</MenuItem>
        <MenuItem value={"inaktiv"}>Inaktive Fälle</MenuItem>
        <MenuItem value={"gewonnen"}>Gewonnene Fälle</MenuItem>
        <MenuItem value={"verloren"}>Verlorene Fälle</MenuItem>
      </ValidatorSelect>
    );
  };

  let techStatusBarVisible = false;

  if (techStatus) {
    const { technicalError, release } = techStatus;
    techStatusBarVisible = (technicalError && technicalError.isActive) || release;
  }

  if (isPartnerRegistryLoading || !productUrlPart || !caseType || !(productUrlPart in refinements)) {
    return null;
  }

  return (
    <Box sx={techStatusBarVisible ? boardPageStyles.outerContainerWithTechStatus : boardPageStyles.outerContainer}>
      <RightBarPlaceholder />
      <InstantSearch searchClient={searchClient} indexName={ALGOLIA_INDEX}>
        <Configure filters={filterString} />
        <Grid
          container
          sx={{
            justifyContent: "flex-end",
            alignItems: "center"
          }}>
          <Grid
            size={{
              xs: 12,
              md: 3
            }} />
          <Grid
            sx={boardPageStyles.headline}
            size={{
              xs: 12,
              md: 6
            }}>
            <Typography variant={"h1"}>Übersicht {getBoardHeadline(productUrlPart)}</Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <Grid container sx={{
              justifyContent: "flex-end"
            }}>
              <Grid>
                <Grid container sx={{
                  alignItems: "center"
                }}>
                  <Grid>
                    <CaseTypeSelection productUrlPart={productUrlPart} caseType={caseType} />
                  </Grid>
                  <Grid>
                    <BoardRefinements refinements={refinements[productUrlPart as keyof AlgoliaRefinements]} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <LegalbirdHits stages={getStagesForBoard(productUrlPart)} />
      </InstantSearch>
    </Box>
  );
};

export default BoardPage;
